import React, { useState, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import moment from 'moment';
import BarChart from './BarChart';
import StackedBarChart from './StackedBarChart';
import LineChart from './LineChart';

try {
  ChartJS.register(
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
  );
} catch (error) {
  console.error('Chart.js registration failed:', error);
}

const Chart = ({
  classNames = '',
  currencyCode = null,
  data = null,
  datasets = null,
  locale = null,
  preFetchedLabels = null,
  type = 'line',
  options = null
}) => {
  const [labels, setLabels] = useState([]);
  const [hasError, setHasError] = useState(false);

  // Check Canvas support (only in browser, not in tests)
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          console.error('Canvas not supported');
          setHasError(true);
        }
      } catch (error) {
        console.error('Canvas check failed:', error);
        setHasError(true);
      }
    }
  }, []);

  const chartData = {
    labels,
    datasets
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (data) {
      const labelsArr = [];
      data?.created_date?.map((date) => {
        const formattedDate = moment(date).locale('en').format('MMM D, YYYY');
        return labelsArr.push(formattedDate);
      });
      setLabels(labelsArr);
    }
    if (preFetchedLabels) {
      if (type === 'bar' || type === 'stacked-bar') {
        const labelsArr = [];
        preFetchedLabels?.map((label) => {
          if (typeof label === 'string' && /^\d{4}-\d{2}-\d{2}/.test(label)) {
            const formattedDate = moment(label).locale('en').format('MMM D, YYYY');
            return labelsArr.push(formattedDate);
          }
          return labelsArr.push(label);
        });
        return setLabels(labelsArr);
      }
      const labelsArr = [];
      preFetchedLabels?.map((label) => {
        const formattedDate = moment(label).locale('en').format('MMM D, YYYY');
        return labelsArr.push(formattedDate);
      });
      setLabels(labelsArr);
    }
  }, [data, preFetchedLabels]);

  if (hasError) {
    return (
      <div className="p-4 text-center text-red-600">
        Your browser does not support charts. Please update your browser.
      </div>
    );
  }

  if (type === 'stacked-bar') {
    return (
      <StackedBarChart
        chartData={chartData}
        classNames={classNames}
      />
    );
  }

  if (type === 'bar') {
    return (
      <BarChart
        chartData={chartData}
        classNames={classNames}
        currencyCode={currencyCode}
        locale={locale}
      />
    );
  }

  return <LineChart chartData={chartData} />;
};

export default Chart;

Chart.propTypes = {
  classNames: PropTypes.string,
  currencyCode: PropTypes.string,
  data: PropTypes.instanceOf(Object),
  datasets: PropTypes.instanceOf(Array),
  locale: PropTypes.string,
  preFetchedLabels: PropTypes.instanceOf(Array),
  type: PropTypes.oneOf(['bar', 'line', 'stacked-bar']),
  options: PropTypes.instanceOf(Object)
};
